import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useBulkAssignCertifications } from "@/hooks/use-persons";

interface PersonCertificationBulkAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** People the certification will be assigned to. */
  personIds: string[];
  /** Called after a successful assign — the caller clears its selection. */
  onAssigned: () => void;
}

/**
 * Admin assigns ONE catalog certification to every currently-selected person.
 * Mirrors {@link PersonCertificationAssignDialog}'s fields (certification +
 * initial status + optional expiry + notes); people who already hold it are
 * skipped server-side and reported in the success toast.
 */
export function PersonCertificationBulkAssignDialog({
  open,
  onOpenChange,
  personIds,
  onAssigned,
}: PersonCertificationBulkAssignDialogProps) {
  const bulkAssign = useBulkAssignCertifications();

  const [certificationId, setCertificationId] = useState("");
  const [status, setStatus] = useState<"assigned" | "started" | "completed">("assigned");
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");

  function reset() {
    setCertificationId("");
    setStatus("assigned");
    setExpiresAt("");
    setNotes("");
  }

  // Clear the form whenever the dialog closes so a reopen starts blank.
  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!certificationId || personIds.length === 0) return;
    await bulkAssign.mutateAsync({
      personIds,
      certificationId,
      status,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      notes: notes.trim() || null,
    });
    handleOpenChange(false);
    onAssigned();
  }

  const count = personIds.length;
  const peopleLabel = `${count} ${count === 1 ? "person" : "people"}`;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign certification to {peopleLabel}</DialogTitle>
          <DialogDescription>
            Pick a certification from the catalog and the initial lifecycle state. People who
            already hold it are skipped.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="bulk-certification">Certification</Label>
            <EntityCombobox
              entity="certifications"
              value={certificationId || null}
              onChange={(v) => setCertificationId(v ?? "")}
              filters={{ status: "active" }}
              placeholder="Pick a certification"
              searchPlaceholder="Search certifications…"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bulk-status">Initial status</Label>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as "assigned" | "started" | "completed")}
            >
              <SelectTrigger id="bulk-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="started">Started</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bulk-expires">Expires (optional)</Label>
            <Input
              id="bulk-expires"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bulk-notes">Notes (optional)</Label>
            <Textarea
              id="bulk-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any context worth recording"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!certificationId || bulkAssign.isPending}>
              {bulkAssign.isPending ? "Assigning…" : `Assign to ${peopleLabel}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
